'use client';

import { useEffect, useState, type ReactNode } from 'react';

/**
 * Client-side user-state hydration primitive. Renders a per-user slot
 * inside an otherwise-cached page without poisoning the edge cache:
 * the page ships HTML containing a fallback (skeleton or CTA), then
 * after mount this component runs the supplied loader and swaps in
 * the real data.
 *
 * <h3>Why this lives at the platform layer</h3>
 * <ul>
 *   <li>Every user-state block needs the same machinery (loading /
 *       error states, AbortController on unmount, page-scoped dedup of
 *       concurrent fetches with the same key, re-fetch when the
 *       {@code cacheKey} changes).</li>
 *   <li>Block authors should only have to supply the {@code cacheKey},
 *       the fetcher, and the fallback markup.</li>
 * </ul>
 *
 * <h3>Why fetchers cross the RSC boundary as props</h3>
 *
 * This file is {@code 'use client'}. A block's island wrapper is also
 * {@code 'use client'}. The fetcher closure is constructed in client
 * scope and passed between two client components — that's legal. (A
 * Server Component cannot pass a function to a Client Component as a
 * prop, which is why each block has its OWN island wrapper rather
 * than the server file invoking {@link Island} directly with a
 * fetcher.)
 *
 * <h3>Cache semantics</h3>
 * <ul>
 *   <li>Page-scoped {@code Map<cacheKey, Promise>}. Two Islands with
 *       the same {@code cacheKey} share a single in-flight fetch.</li>
 *   <li>Survives soft nav (module isn't re-evaluated); clears on full
 *       nav (module is fresh).</li>
 *   <li>When the {@code cacheKey} changes (e.g. user signs in and the
 *       key now includes user.id), a NEW fetch runs and overwrites the
 *       cache entry. Stale data from the previous identity isn't
 *       shown.</li>
 * </ul>
 *
 * <h3>Error handling</h3>
 * <ul>
 *   <li>One fetch attempt, no retry. If the fetch rejects,
 *       {@code errorFallback} renders (defaults to {@code null} — the
 *       block disappears). Block authors can supply a
 *       "couldn't load — try again" affordance if they want.</li>
 *   <li>The user can hard-reload to retry. Auto-retry on transient
 *       errors creates retry storms when the backend is degraded;
 *       we'd rather be deliberate.</li>
 * </ul>
 */
interface IslandProps<D> {
  /** Stable cache/dedup key. Two Islands with the same key share one
   *  in-flight fetch. The key MUST include any value that shapes the
   *  fetch result — user.id, source, limit, etc. When the key changes,
   *  the previous fetch is abandoned and a new one runs. */
  cacheKey: string;

  /** Client-side fetcher. Receives an {@link AbortSignal} for cleanup
   *  on unmount. Should attach the signal to its {@code fetch()} call
   *  (or any helper that accepts a signal). */
  load: (signal: AbortSignal) => Promise<D>;

  /** Rendered while the fetch is in flight. Should be a skeleton or a
   *  CTA card sized to roughly match the loaded state so the page
   *  doesn't shift on hydration. */
  fallback: ReactNode;

  /** Renders with the resolved data. */
  children: (data: D) => ReactNode;

  /** Rendered if the fetch rejects. Default: {@code null} (block hides). */
  errorFallback?: ReactNode;
}

// Page-scoped cache. Module-level so it survives across component
// remounts within the same page session. Cleared by the browser when
// the JS context resets (full nav, page reload).
const cache = new Map<string, Promise<unknown>>();

export function Island<D>({
  cacheKey,
  load,
  fallback,
  children,
  errorFallback = null,
}: IslandProps<D>) {
  const [state, setState] = useState<
    { kind: 'loading' } | { kind: 'loaded'; data: D } | { kind: 'error' }
  >({ kind: 'loading' });

  useEffect(() => {
    const ctrl = new AbortController();

    setState({ kind: 'loading' });

    let promise = cache.get(cacheKey) as Promise<D> | undefined;
    if (!promise) {
      promise = load(ctrl.signal);
      cache.set(cacheKey, promise);
    }

    promise
      .then((d) => {
        if (!ctrl.signal.aborted) setState({ kind: 'loaded', data: d });
      })
      .catch(() => {
        if (!ctrl.signal.aborted) setState({ kind: 'error' });
        if (cache.get(cacheKey) === promise) cache.delete(cacheKey);
      });

    return () => ctrl.abort();
  }, [cacheKey, load]);

  if (state.kind === 'loading') return <>{fallback}</>;
  if (state.kind === 'error') return <>{errorFallback}</>;
  return <>{children(state.data)}</>;
}

/**
 * Manually drop a cached entry. Use case: a mutation invalidates a
 * user-state fetch (e.g. user completes a course → ContinueLearning
 * should re-fetch). Any Islands mounted with the cleared key will
 * continue showing stale data until they remount; the next Island
 * with that key fetches fresh.
 */
export function invalidateIsland(cacheKey: string): void {
  cache.delete(cacheKey);
}
